import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { fromEvent, map, Observable, Subscription, throttleTime } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { FetchAllBrands, FetchAllBrandsSuccess, FetchBrands } from 'src/app/features/brands/store/brands.actions';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { FetchAllCategories, FetchAllCategoriesSuccess } from 'src/app/features/categories/store/categories.actions';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { FetchAllTags, FetchAllTagsSuccess } from 'src/app/features/tags/store/tags.actions';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { IInventoryFilter } from '../../models/products.models';
import {
  parseInventoryFilterRequest,
  refillInventoryFilter,
} from 'src/app/shared/utils/helpers';
import { FormControl, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
  selector: 'app-filter-dialog',
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss'],
})
export class FilterDialogComponent implements OnInit {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  @ViewChild('brandsAutoComplete') brandsAutoComplete?: MatAutocomplete;

  currentFilter: Observable<IInventoryFilter> = this.store.select(
    (state) => state.products.productFilterState.advanced
  );
  inventoryFilters = LocalStorageService.get('inventoryFilters');

  brands$: Observable<IBrand[]> = this.store.select(
    (state) => [...state.brands.brands].sort((a, b) => a.name.localeCompare(b.name))
  );
  filteredBrands$: Observable<IBrand[]> = this.brands$;
  brandsFilterKey: string = '';
  private brandsPageNumber = 1;
  private readonly brandsPageSize = 50;
  private brandsHasMore = true;
  private brandsLoading = false;
  private brandsScrollSub?: Subscription;

  categories$ = this.store.select((state) => [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)));
  filteredCategories$ = this.categories$;
  categoriesFilterKey: string = '';

  tags$ = this.store.select((state) => [...state.tags.allTags].sort((a, b) => a.name.localeCompare(b.name)));
  filteredTags$ = this.tags$;
  tagsFilterKey: string = '';

  statuses: { label: string; value: string }[] = [
    { label: 'All', value: '' },
    { label: 'Published', value: 'Published' },
    { label: 'Draft', value: 'draft' },
  ];

  hasVariants: { label: string; value: boolean | null }[] = [
    {
      label: 'All',
      value: null,
    },
    {
      label: 'With variants',
      value: true,
    },
    {
      label: 'Without variant',
      value: false,
    },
  ];

  selectedGroup = 'product_info';

  filter: IInventoryFilter = refillInventoryFilter();
  filterName = '';
  toggleSaveFilterForm = false;
  filterNameControl = new FormControl('', Validators.required);
  private subscriptions: Subscription[] = [];
  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    let sub1: Subscription | undefined;
    if(this.data){
      this.filter = refillInventoryFilter();
      this.filter.productsInfoFilter.name = this.data.filter?.name ?? undefined;
      this.filter.productsInfoFilter.sku = this.data.filter?.sku ?? undefined;
      this.filter.productsInfoFilter.barcode = this.data.filter?.barcode ?? undefined;
      this.filter.productsInfoFilter.brands = this.data.filter?.brands && this.data.filter?.brands.length > 0 ? this.data.filter?.brands : [];
      this.filter.productsInfoFilter.status = this.data.filter?.status ?? undefined;
      this.filter.productsInfoFilter.has_variants = this.data.filter?.has_variants === 'true' ? true : this.data.filter?.has_variants === 'false' ? false : undefined;
      this.filter.organizeFilter.categories = this.data.filter?.categories && this.data.filter?.categories.length > 0 ? this.data.filter?.categories : [];
      this.filter.organizeFilter.tags = this.data.filter?.tags && this.data.filter?.tags.length > 0 ? this.data.filter?.tags : [];
      this.filter.organizeFilter.has_catalogs = this.data.filter?.has_catalogs === 'true' ? true : this.data.filter?.has_catalogs === 'false' ? false : undefined;
      this.filter.organizeFilter.number_of_catalogs = this.data.filter?.number_of_catalogs ?? undefined;
      this.filter.inventoryFilter.sales_price_min = this.data.filter?.sales_price_min ?? undefined;
      this.filter.inventoryFilter.sales_price_max = this.data.filter?.sales_price_max ?? undefined;
      this.filter.inventoryFilter.cost_min = this.data.filter?.cost_min ?? undefined;
      this.filter.inventoryFilter.cost_max = this.data.filter?.cost_max ?? undefined;
      this.filter.inventoryFilter.quantity_min = this.data.filter?.quantity_min ?? undefined;
      this.filter.inventoryFilter.quantity_max = this.data.filter?.quantity_max ?? undefined;
      this.filter.inventoryFilter.reorder_point_min = this.data.filter?.reorder_point_min ?? undefined;
      this.filter.inventoryFilter.reorder_point_max = this.data.filter?.reorder_point_max ?? undefined;
      this.filter.inventoryFilter.expiry_date_min = this.data.filter?.expiry_date_min ?? undefined;
      this.filter.inventoryFilter.expiry_date_max = this.data.filter?.expiry_date_max ?? undefined;
    } else {
      sub1 = this.currentFilter.subscribe((filter: IInventoryFilter) => {
        this.filter = refillInventoryFilter(filter);
      });
    }
    // Lazy-load brands (100 per page) instead of loading the full list.
    this.filterBrands();
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchAllTags({}));

    const subBrandsMeta = this.store.select((state) => state.brands.metadata).subscribe((metadata) => {
      if (!metadata) {
        this.brandsHasMore = true;
        return;
      }
      this.brandsHasMore = metadata.page < metadata.pages;
    });
    const subBrandsLoading = this.store.select((state) => state.brands.loading).subscribe((loading) => {
      this.brandsLoading = loading;
    });

    this.subscriptions.push(subBrandsMeta, subBrandsLoading);
    if (sub1) {
      this.subscriptions.push(sub1);
    }
  }

  getFiltersCount(): number {
    return parseInventoryFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters(): void {
    this.filter = refillInventoryFilter();
    this.filterName = '';
  }

  filterBrands(): void {
    const keyword = (this.brandsFilterKey ?? '').trim();

    // Reset paging for a new search.
    this.brandsPageNumber = 1;
    this.store.dispatch(
      FetchBrands({
        page: { number: this.brandsPageNumber, size: this.brandsPageSize },
        key: keyword,
        append: false,
      })
    );

    this.filteredBrands$ = this.brands$.pipe(
      map((brands) =>
        brands.filter((brand) => {
          const matchesKeyword =
            !keyword ||
            brand.name?.toLocaleLowerCase().includes(keyword.toLocaleLowerCase());
          const notSelected = !this.filter?.productsInfoFilter?.brands?.some(
            (b) => b.id === brand.id
          );
          return matchesKeyword && notSelected;
        })
      )
    );
  }

  private loadMoreBrands(): void {
    if (this.brandsLoading || !this.brandsHasMore) return;

    const keyword = (this.brandsFilterKey ?? '').trim();
    this.brandsPageNumber += 1;
    this.store.dispatch(
      FetchBrands({
        page: { number: this.brandsPageNumber, size: this.brandsPageSize },
        key: keyword,
        append: true,
      })
    );
  }

  onBrandsAutocompleteOpened(): void {
    // The panel element is created in an overlay; wait a tick before querying the DOM.
    setTimeout(() => {
      const panelId = this.brandsAutoComplete?.id;
      if (!panelId) return;

      const panelEl = document.getElementById(panelId);
      if (!panelEl) return;

      this.brandsScrollSub?.unsubscribe();
      this.brandsScrollSub = fromEvent(panelEl, 'scroll')
        .pipe(
          throttleTime(100, undefined, { leading: true, trailing: true }),
          map(() => {
            const thresholdPx = 24;
            const atBottom =
              panelEl.scrollTop + panelEl.clientHeight >= panelEl.scrollHeight - thresholdPx;
            return atBottom;
          })
        )
        .subscribe((atBottom) => {
          if (atBottom) this.loadMoreBrands();
        });
    });
  }

  onBrandsAutocompleteClosed(): void {
    this.brandsScrollSub?.unsubscribe();
    this.brandsScrollSub = undefined;
  }

  filterCategories(): void {
    this.filteredCategories$ = this.categories$.pipe(
      map((categories) =>
        categories.filter(
          (category) =>
            category.name
              .toLocaleLowerCase()
              ?.includes(this.categoriesFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.organizeFilter?.categories?.find(
              (c) => c.id === category.id
            )
        )
      )
    );
  }

  filterTags(): void {
    this.filteredTags$ = this.tags$.pipe(
      map((tags) =>
        tags.filter(
          (tag) =>
            tag.name
              .toLocaleLowerCase()
              ?.includes(this.tagsFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.organizeFilter?.tags?.find((t) => t.id === tag.id)
        )
      )
    );
  }

  selectOption(type: string, event: MatAutocompleteSelectedEvent){
    switch (type) {
      case 'brand':
        const brand: IBrand = event.option.value;
        this.filter?.productsInfoFilter?.brands?.push(brand);
        setTimeout(() => {
          this.brandsFilterKey = '';
          this.filterBrands();
        });
        break;
      case 'category':
        const category: ICategory = event.option.value
        this.filter?.organizeFilter?.categories?.push(category);
        setTimeout(() => {
          this.categoriesFilterKey = '';
          this.filterCategories();
        });
        break;
      case 'tag':
        const tag: ITag = event.option.value
        this.filter?.organizeFilter?.tags?.push(tag);
        setTimeout(() => {
          this.tagsFilterKey = '';
          this.filterTags()
        });
        break;
    }
  }
  getBrandsNames(): string {
    return (this.filter?.productsInfoFilter?.brands || [])
      .map((brand) => brand.name)
      .join(', ');
  }

  getCategoriesNames(): string {
    return (this.filter?.organizeFilter?.categories || [])
      .map((category) => category.name)
      .join(', ');
  }

  getTagsNames(): string {
    return (this.filter?.organizeFilter?.tags || [])
      .map((tag) => tag.name)
      .join(', ');
  }

  toggleSaveFilter(): void {
    this.toggleSaveFilterForm = !this.toggleSaveFilterForm;
  }

  save(): boolean | void {
    if(!this.filterNameControl.valid){
      this.filterNameControl.markAsTouched();
      return false
    }
    if (!this.inventoryFilters?.filters)
      this.inventoryFilters = {
        filters: [{ name: this.filterName, filter: this.filter }],
      };
    else {
      const filter = this.inventoryFilters.filters?.find(
        (f: any) => f.name.toLowerCase() === this.filterName.toLowerCase()
      );
      if (filter)
        this.inventoryFilters.filters = this.inventoryFilters.filters?.map((f: any) => {
          if (f.name === this.filterName) return {name: this.filterName, filter: this.filter};
          return f;
        });
      else
        this.inventoryFilters.filters.push({
          name: this.filterName,
          filter: this.filter,
        });
    }
    LocalStorageService.put('inventoryFilters', this.inventoryFilters);
    this.toggleSaveFilter();
  }

  setFilter(filter: any): void {
    if(filter){
      this.filterName = filter.name;
      this.filter = refillInventoryFilter(filter.filter);
    }
  }

  deleteFilter(filter: any): void {
    this.inventoryFilters.filters = this.inventoryFilters.filters?.filter(
      (f: any) => f.name !== filter.name
    );
    LocalStorageService.put('inventoryFilters', this.inventoryFilters);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }

  ngOnDestroy(): void {
    this.onBrandsAutocompleteClosed();
    this.subscriptions.forEach((sub => sub.unsubscribe()))
  }
}
